// Search-state for the price-modifiers index route. The route in AppRouter.tsx
// owns the validateSearch schema and stripSearchParams middleware; this hook is
// a thin, typed surface over TanStack's useSearch / useNavigate so consumers
// don't touch the router primitives directly. Switching tab resets the page so
// a previous tab's pagination never bleeds into the new one.

// External
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

// Internal
import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';

export type PriceModifierTab = 'discounts' | 'surcharges';

export interface PriceModifiersSearchParams {
  tab: PriceModifierTab;
  q: string;
  page: number;
  perPage: number;
}

export const PRICE_MODIFIERS_SEARCH_DEFAULTS: PriceModifiersSearchParams = {
  tab: 'discounts',
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
};

const PRICE_MODIFIERS_ROUTE = '/price_modifiers' as const;

const VALID_TABS = new Set<PriceModifierTab>(['discounts', 'surcharges']);

// validateSearch handler — coerces unknown URL input into the typed shape.
export function parsePriceModifiersSearch(
  raw: Record<string, unknown>,
): PriceModifiersSearchParams {
  const tab: PriceModifierTab = VALID_TABS.has(raw.tab as PriceModifierTab)
    ? (raw.tab as PriceModifierTab)
    : PRICE_MODIFIERS_SEARCH_DEFAULTS.tab;
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  return {
    tab,
    q: typeof raw.q === 'string' ? raw.q : PRICE_MODIFIERS_SEARCH_DEFAULTS.q,
    page: Number.isFinite(page) && page > 0 ? page : PRICE_MODIFIERS_SEARCH_DEFAULTS.page,
    perPage:
      Number.isFinite(perPage) && perPage > 0
        ? perPage
        : PRICE_MODIFIERS_SEARCH_DEFAULTS.perPage,
  };
}

export function usePriceModifiersRouterSearch() {
  const search = useSearch({ from: PRICE_MODIFIERS_ROUTE });
  const tanNavigate = useNavigate({ from: PRICE_MODIFIERS_ROUTE });

  const navigate = useCallback(
    (updates: Partial<PriceModifiersSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setTab: (tab: PriceModifierTab) => navigate({ tab, page: 1 }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
    }),
    [search, navigate],
  );
}
